import { Alert, Box, Card, CardContent, Chip, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import { Link } from 'react-router-dom';
import { callAction } from './dataProvider';
import { flashSx, POLL_MS } from './newArrivals';

/**
 * The landing page: how the queue looks right now.
 *
 * Counts rather than money, on purpose. Order totals have only been recorded
 * since the shop started keeping them, so a revenue chart across any
 * historical window would draw a line at zero and read as a collapse in trade
 * rather than a gap in the data. Revenue appears as a single figure that says
 * how many orders it covers.
 */

interface Summary {
  open: number;
  openAllTime: number;
  today: number;
  last7: number;
  last30: number;
  revenue30: number | null;
  revenue30Orders: number;
  revenueAll: number | null;
  revenueAllOrders: number;
  daily: { date: string; orders: number }[];
  topProducts: { productId: number | null; code: string | null; name: string | null; quantity: number }[];
  latest: {
    id: number;
    customer: string;
    organizationName: string | null;
    ordered: string | null;
    created: string;
    fulfilled: boolean;
    totalWithVat: number | null;
    paymentId: string | null;
    paymentStatus: string | null;
    isNew: boolean;
  }[];
}

const EUR = new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' });
const DATE = new Intl.DateTimeFormat('sk-SK', { day: 'numeric', month: 'numeric' });

export function Dashboard() {
  const notify = useNotify();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [failed, setFailed] = useState(false);


  useEffect(() => {
    const load = (initial: boolean) => {
      callAction('dashboard', 'GET' as never)
        .then(value => {
          setSummary(value as Summary);
          // A later poll that succeeds replaces the warning with the figures.
          setFailed(false);
        })
        .catch(() => {
          // Only the first failure is worth interrupting for; a poll that
          // fails while the tab sits in the background should stay quiet.
          if (!initial) return;
          setFailed(true);
          notify('Prehľad sa nepodarilo načítať.', { type: 'warning' });
        });
    };

    load(true);
    const timer = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(timer);
  }, []);

  if (failed) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Prehľad sa nepodarilo načítať. Objednávky nájdete v sekcii Objednávky.
      </Alert>
    );
  }

  if (summary == null) return <Typography sx={{ p: 2 }}>Načítava sa…</Typography>;



  return (
    <Box sx={{ mt: 1 }}>
      <Title title="Prehľad" />

      <Grid container spacing={2}>
        <Stat
          label="Čaká na vybavenie"
          value={summary.open}
          highlight={summary.open > 0}
          to="/orders"
          note={
            summary.openAllTime > summary.open
              ? `+ ${summary.openAllTime - summary.open} starších ako 90 dní`
              : undefined
          }
        />
        <Stat label="Dnes" value={summary.today} />
        <Stat label="Posledných 7 dní" value={summary.last7} />
        <Stat label="Posledných 30 dní" value={summary.last30} />
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Objednávky za posledných 30 dní
              </Typography>
              <DailyChart daily={summary.daily} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Tržba za 30 dní</Typography>
              {summary.revenue30 == null ? (
                /* Nothing in the window yet. Rather than an empty card, show
                   what has been recorded so far - the thirty-day figure only
                   fills in once orders placed since totals started being kept
                   fall inside it. */
                summary.revenueAll == null ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Zatiaľ žiadna objednávka s uloženou sumou. Sumy sa evidujú až od zavedenia tejto
                    funkcie — staršie objednávky ich nemajú, ich skutočná suma je v Stripe.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="h4" sx={{ mt: 1 }}>{EUR.format(summary.revenueAll)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      za celé obdobie evidencie ({summary.revenueAllOrders} objednávok) — za posledných
                      30 dní zatiaľ žiadna objednávka s uloženou sumou
                    </Typography>
                  </>
                )
              ) : (
                <>
                  <Typography variant="h4" sx={{ mt: 1 }}>{EUR.format(summary.revenue30)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    z {summary.revenue30Orders} objednávok so známou sumou
                    {summary.revenue30Orders < summary.last30 && (
                      <> — {summary.last30 - summary.revenue30Orders} z posledných 30 dní sumu nemá</>
                    )}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="overline" color="text.secondary">Posledné nevybavené objednávky</Typography>
                <Typography variant="caption" component={Link} to="/orders">všetky</Typography>
              </Stack>

              {summary.latest.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Všetko je vybavené.
                </Typography>
              ) : (
                <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
                  {summary.latest.map(order => (
                    <Stack
                      key={order.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      component={Link}
                      to={`/orders/${order.id}/show`}
                      sx={{
                        py: 1,
                        px: 1,
                        borderRadius: 1,
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': { bgcolor: 'action.hover' },
                        // The same watermark as the queue, so an order seen on
                        // one screen is not announced again on the other.
                        ...(order.isNew ? flashSx : {})
                      }}
                    >
                      <Stack sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          č. {order.id} · {order.customer}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {formatDate(order.ordered ?? order.created)}
                          {order.organizationName != null && <> · {order.organizationName}</>}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {order.paymentStatus === 'succeeded' && (
                          <Chip size="small" color="success" variant="outlined" label="Zaplatená" />
                        )}
                        {order.paymentStatus === 'manual' && (
                          <Chip size="small" color="success" variant="outlined" label="Zaplatená (ručne)" />
                        )}
                        {order.paymentStatus == null && (
                          <Chip size="small" color="warning" variant="outlined" label="Nezaplatená" />
                        )}
                        {order.totalWithVat != null && (
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {EUR.format(order.totalWithVat)}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Najpredávanejšie za 90 dní
              </Typography>
              {summary.topProducts.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Žiadne položky.
                </Typography>
              ) : (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {summary.topProducts.map((product, index) => (
                    <TopProduct
                      key={product.productId ?? `row-${index}`}
                      product={product}
                      max={summary.topProducts[0]?.quantity ?? 1}
                    />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function Stat({ label, value, highlight = false, to, note }: {
  label: string;
  value: number;
  highlight?: boolean;
  to?: string;
  note?: string;
}) {
  const card = (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        ...(highlight ? { borderColor: 'warning.main' } : {}),
        ...(to != null ? { '&:hover': { bgcolor: 'action.hover' } } : {})
      }}
    >
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" color={highlight ? 'warning.main' : 'text.primary'}>{value}</Typography>
        {/* The historical backlog, kept out of the headline figure: it is
            thousands of orders nobody is going to work through, and showing it
            as today's workload would make the queue look hopeless. */}
        {note != null && (
          <Typography variant="caption" color="text.secondary">{note}</Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Grid item xs={6} md={3}>
      {to == null ? card : <Box component={Link} to={to} sx={{ textDecoration: 'none' }}>{card}</Box>}
    </Grid>
  );
}

/**
 * Orders per day, drawn by hand.
 *
 * Thirty bars do not justify a charting library and the megabyte it adds to a
 * bundle that is already large. Every day in the window is present, including
 * the empty ones, so a quiet week looks quiet instead of being closed up.
 */
function DailyChart({ daily }: { daily: { date: string; orders: number }[] }) {
  const max = Math.max(1, ...daily.map(day => day.orders));

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ height: 140 }}>
        {daily.map(day => (
          <Tooltip key={day.date} title={`${formatDate(day.date)}: ${day.orders}`} arrow>
            <Box
              sx={{
                flex: 1,
                minWidth: 4,
                // A day with no orders still gets a sliver, so the axis reads
                // as a row of days rather than a gap in the chart.
                height: `${Math.max((day.orders / max) * 100, 2)}%`,
                bgcolor: day.orders === 0 ? 'action.disabledBackground' : 'primary.main',
                borderRadius: 0.5,
                transition: 'background-color .15s',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            />
          </Tooltip>
        ))}
      </Stack>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {formatDate(daily[0]?.date)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          najviac za deň: {max}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(daily[daily.length - 1]?.date)}
        </Typography>
      </Stack>
    </Box>
  );
}

function TopProduct({ product, max }: {
  product: { productId: number | null; code: string | null; name: string | null; quantity: number };
  max: number;
}) {
  const label = product.name ?? product.code ?? `#${product.productId ?? '?'}`;

  // A row without a product id has nothing to open - the line survived but the
  // product behind it did not.
  const body = (
    <Box>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {product.quantity} ks
        </Typography>
      </Stack>
      <Box sx={{ mt: 0.5, height: 4, bgcolor: 'action.hover', borderRadius: 2 }}>
        <Box
          sx={{
            width: `${Math.max((product.quantity / max) * 100, 2)}%`,
            height: '100%',
            bgcolor: 'primary.main',
            borderRadius: 2
          }}
        />
      </Box>
    </Box>
  );

  if (product.productId == null) return body;

  return (
    <Box
      component={Link}
      to={`/products/${product.productId}`}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: 1,
        p: 0.5,
        mx: -0.5,
        '&:hover': { bgcolor: 'action.hover' }
      }}
    >
      {body}
    </Box>
  );
}

function formatDate(value: string | undefined): string {
  if (value == null) return '';
  return DATE.format(new Date(value));
}
